/**
 * Shared calculation utilities for building templates
 *
 * Provides common math functions for:
 * - Wall totals
 * - Usable space calculations
 * - Unit width distribution
 */

/**
 * Calculate total wall thickness for a row of units
 *
 * @param unitCount - Number of units in the row
 * @param wallThickness - Thickness of each wall in feet
 * @returns Total wall thickness (includes exterior walls + partitions)
 */
export function calculateTotalWallThickness(
  unitCount: number,
  wallThickness: number
): number {
  // Exterior walls (2) + partition walls (unitCount - 1)
  // Total walls = unitCount + 1
  return (unitCount + 1) * wallThickness
}

/**
 * Calculate usable interior length after accounting for walls
 *
 * @param buildingLength - Total building length in feet
 * @param unitCount - Number of units
 * @param wallThickness - Wall thickness in feet
 * @returns Usable length for units
 */
export function calculateUsableLength(
  buildingLength: number,
  unitCount: number,
  wallThickness: number
): number {
  const totalWalls = calculateTotalWallThickness(unitCount, wallThickness)
  return buildingLength - totalWalls
}

/**
 * Calculate equal unit widths from building dimensions
 *
 * @param buildingLength - Total building length in feet
 * @param unitCount - Number of units
 * @param wallThickness - Wall thickness in feet
 * @returns Array of equal unit widths
 */
export function calculateEqualUnitWidths(
  buildingLength: number,
  unitCount: number,
  wallThickness: number
): number[] {
  const usableLength = calculateUsableLength(buildingLength, unitCount, wallThickness)
  const unitWidth = usableLength / unitCount

  return Array(unitCount).fill(unitWidth)
}

/**
 * Result of unit width calculation with balance checking
 */
export interface WidthCalculationResult {
  /** Calculated widths for each unit */
  widths: number[]
  /** Whether total matches building length (within tolerance) */
  isBalanced: boolean
  /** Difference in feet (positive = over, negative = under) */
  difference_ft: number
  /** Suggested building length to make balanced */
  suggestedBuildingLength: number
  /** Total width used by units */
  totalUnitsWidth: number
  /** Total wall thickness */
  totalWallsWidth: number
}

/**
 * Calculate unit widths with override support and balance checking
 *
 * @param buildingLength - Total building length in feet
 * @param unitCount - Number of units
 * @param wallThickness - Wall thickness in feet
 * @param overrides - Optional width overrides for specific units
 * @param tolerance - Balance tolerance in feet (default 0.1)
 * @returns Calculation result with balance status
 */
export function calculateUnitWidthsWithOverrides(
  buildingLength: number,
  unitCount: number,
  wallThickness: number,
  overrides?: Array<{ index: number; width_ft: number }>,
  tolerance: number = 0.1
): WidthCalculationResult {
  // Start with equal distribution
  const defaultWidths = calculateEqualUnitWidths(buildingLength, unitCount, wallThickness)

  // Apply overrides
  const widths = defaultWidths.map((defaultWidth, index) => {
    const override = overrides?.find(o => o.index === index)
    return override?.width_ft ?? defaultWidth
  })

  // Calculate totals
  const totalWallsWidth = calculateTotalWallThickness(unitCount, wallThickness)
  const totalUnitsWidth = widths.reduce((sum, w) => sum + w, 0)
  const totalUsed = totalUnitsWidth + totalWallsWidth
  const difference = totalUsed - buildingLength

  return {
    widths,
    isBalanced: Math.abs(difference) < tolerance,
    difference_ft: difference,
    suggestedBuildingLength: totalUsed,
    totalUnitsWidth,
    totalWallsWidth,
  }
}

/**
 * Redistribute unit widths to fit within building length
 * Keeps overridden units fixed, adjusts non-overridden units
 *
 * @param buildingLength - Target building length
 * @param unitCount - Number of units
 * @param wallThickness - Wall thickness
 * @param overrides - Fixed width overrides
 * @returns Redistributed widths that sum to building length
 */
export function redistributeUnitWidths(
  buildingLength: number,
  unitCount: number,
  wallThickness: number,
  overrides?: Array<{ index: number; width_ft: number }>
): number[] {
  const totalWalls = calculateTotalWallThickness(unitCount, wallThickness)
  const usableLength = buildingLength - totalWalls

  // Count fixed vs flexible units
  const overrideMap = new Map(overrides?.map(o => [o.index, o.width_ft]) ?? [])
  let fixedWidth = 0
  let flexibleCount = 0

  for (let i = 0; i < unitCount; i++) {
    if (overrideMap.has(i)) {
      fixedWidth += overrideMap.get(i)!
    } else {
      flexibleCount++
    }
  }

  // Calculate width for flexible units
  const remainingWidth = usableLength - fixedWidth
  const flexibleUnitWidth = flexibleCount > 0 ? remainingWidth / flexibleCount : 0

  // Build result array
  return Array.from({ length: unitCount }, (_, i) =>
    overrideMap.get(i) ?? flexibleUnitWidth
  )
}

/**
 * Calculate unit offset positions within building
 *
 * @param widths - Array of unit widths
 * @param wallThickness - Wall thickness
 * @returns Array of X offsets for each unit (from building origin)
 */
export function calculateUnitOffsets(
  widths: number[],
  wallThickness: number
): number[] {
  const offsets: number[] = []
  let currentOffset = wallThickness // Start after first exterior wall

  for (const width of widths) {
    offsets.push(currentOffset)
    currentOffset += width + wallThickness
  }

  return offsets
}
